import type { components } from '@repo/api-types/merchant'
import type { FeeWaiverConfig } from "@/api/coupon"
import { BusinessConfigApi, CashbackCashierApi, ChainEmployeeBindingsApi, Configuration, CouponApi, EmployeeCommissionStatApi, MemberCashierApi, MemberStatApi, MerchantCommissionRuleApi, MerchantCreditChangeRecordApi, MerchantCreditExchangeRecordApi, MerchantCreditProductApi, MerchantCustomerUserApi, MerchantMemberChargeRecordApi, MerchantMemberOptionRecordApi, MerchantMmsMemberLevelApi, MerchantPmsItemApi, MerchantPmsItemCategoryApi, MerchantPmsItemMealAdditionRelApi, MerchantPurchaseApi, MerchantTableBillingRuleApi, MerchantTableRecordApi, MerchantUserApi, MerchantWmsConsumptionOrderApi, MerchantWmsDataControllerApi, MerchantWmsItemCostCardApi, MerchantWmsSkuApi, MerchantWmsSkuCategoryApi, MerchantWmsStockApi, OvertimeServiceFeeCommissionStatApi, ProductCommissionStatApi, StoredValueCardCashierApi } from '@repo/codegen/teashop-merchant-api'
import { MemberAdminApi } from '@repo/codegen/teashop-merchant-api/api/member-admin-api'
import { MemberCashierCompensationApi } from '@repo/codegen/teashop-merchant-api/api/member-cashier-compensation-api'
import { MerchantHeadquarterBusinessReportControllerApi } from '@repo/codegen/teashop-merchant-api/api/merchant-headquarter-business-report-controller-api'
import { createTypedClient } from "@/api/typed/client"
import { getConfig } from "@/api/utils"
import { service2 as service } from "@/utils/service"

const PREFIX = "/teashop_merchant"
const baseOptions = getConfig({
  prefix: PREFIX,
})
const merchantConfig = new Configuration({
  baseOptions,
})

/** 新接口消费入口:类型来自 @repo/api-types(OpenAPI 快照生成),运行时仍走 service2 */
const typedClient = createTypedClient<import('@repo/api-types/merchant').paths>({
  service,
  defaults: {
    baseURL: baseOptions.baseURL,
    headers: baseOptions.headers,
    timeout: baseOptions.timeout,
  },
})

export const overtimeServiceFeeCommissionStatAPI = new OvertimeServiceFeeCommissionStatApi(merchantConfig, baseOptions.baseURL, service)
export const productCommissionStatAPI = new ProductCommissionStatApi(merchantConfig, baseOptions.baseURL, service)
export const employeeCommissionStatAPI = new EmployeeCommissionStatApi(merchantConfig, baseOptions.baseURL, service)

// 契约类型来自 @repo/api-types 生成类型,替代此前按后端 DTO 手写的重复定义
export type EmployeeCommissionRecordGroupedRequest = components["schemas"]["EmployeeCommissionRecordGroupedRequest"]
export type EmployeeCommissionRecordGroup = components["schemas"]["RecordGroup"]
export type EmployeeCommissionRecordGroupedResponse = components["schemas"]["EmployeeCommissionRecordGroupedResponse"]

export function getEmployeeCommissionRecordPage(commissionStatQueryDTO: Record<string, any>) {
  const commissionType = commissionStatQueryDTO?.commissionType
  // URL 带类型用于区分 X-Request-ID，过滤仍以后端 body 为准。
  const url = commissionType == null
    ? '/merchant/dc/employee_commission/record/page'
    : `/merchant/dc/employee_commission/record/page?commissionType=${encodeURIComponent(String(commissionType))}`

  return service.post(url, commissionStatQueryDTO, {
    baseURL: baseOptions.baseURL,
    headers: baseOptions.headers,
    timeout: baseOptions.timeout,
  })
}

export function getEmployeeCommissionRecordGrouped(employeeCommissionRecordGroupedRequest: EmployeeCommissionRecordGroupedRequest) {
  return typedClient.post('/merchant/dc/employee_commission/record/grouped', employeeCommissionRecordGroupedRequest)
}

export interface SearchCashierMembersParams {
  phone?: string
  keyword?: string
  shopId?: number
}

type CashierMemberSearchResponse = components["schemas"]["RCashierMemberSearchResultDTO"]

export type CashierMemberCouponStatus = "unused" | "used" | "expired" | "locked"

export interface CashierMemberCoupon {
  id: number
  memberId?: number
  templateId?: number
  name?: string
  description?: string
  type?: string
  faceValue?: number
  discountRate?: number | string
  minOrderAmount?: number
  status?: CashierMemberCouponStatus | string
  validFrom?: string
  validTo?: string
  usedAt?: string
  lockedOrderId?: number
  redeemCode?: string
  feeWaiverConfig?: FeeWaiverConfig
}

export interface CashierMemberCouponPack {
  items?: CashierMemberCoupon[]
  pageInfo?: {
    page?: number
    pageSize?: number
    total?: number
  }
}

export interface CashierMemberCouponPackResponse {
  code: number
  msg?: string | null
  data?: CashierMemberCouponPack | null
}

export interface ListCashierMemberCouponPackParams {
  memberId: number
  shopId?: number
  status?: CashierMemberCouponStatus
  page?: number
  pageSize?: number
}

export function searchCashierMembers(params: SearchCashierMembersParams) {
  return service.get<CashierMemberSearchResponse>('/cashier/members/search', {
    baseURL: baseOptions.baseURL,
    headers: baseOptions.headers,
    timeout: baseOptions.timeout,
    params,
  })
}

export function listCashierMemberCouponPack(params: ListCashierMemberCouponPackParams) {
  const { memberId, shopId, status = "unused", page = 1, pageSize = 20 } = params
  return service.get<CashierMemberCouponPackResponse>(`/cashier/members/${memberId}/coupon-pack`, {
    baseURL: baseOptions.baseURL,
    headers: baseOptions.headers,
    timeout: baseOptions.timeout,
    params: {
      shopId,
      status,
      page,
      pageSize,
    },
  })
}

export function applyProductCommissionRuleToOrders(ruleId: number, data: Record<string, any>) {
  return service.post(`/merchant/commission/rule/${ruleId}/apply-product-orders`, data, {
    baseURL: baseOptions.baseURL,
    headers: baseOptions.headers,
    timeout: baseOptions.timeout,
  })
}
export const merchantTableRecordAPI = new MerchantTableRecordApi(merchantConfig, baseOptions.baseURL, service)
export const merchantCommissionRuleAPI = new MerchantCommissionRuleApi(merchantConfig, baseOptions.baseURL, service)
export const merchantPmsItemAPI = new MerchantPmsItemApi(merchantConfig, baseOptions.baseURL, service)
export const merchantUserAPI = new MerchantUserApi(merchantConfig, baseOptions.baseURL, service)
export const businessConfigAPI = new BusinessConfigApi(merchantConfig, baseOptions.baseURL, service)
export const merchantPmsItemCategoryAPI = new MerchantPmsItemCategoryApi(merchantConfig, baseOptions.baseURL, service)
export const merchantTableBillingRuleAPI = new MerchantTableBillingRuleApi(merchantConfig, baseOptions.baseURL, service)
export const merchantPmsItemMealAdditionRelAPI = new MerchantPmsItemMealAdditionRelApi(merchantConfig, baseOptions.baseURL, service)
export const merchantWmsItemCostCardAPI = new MerchantWmsItemCostCardApi(merchantConfig, baseOptions.baseURL, service)
export const merchantCreditProductAPI = new MerchantCreditProductApi(merchantConfig, baseOptions.baseURL, service)
export const merchantCustomerUserAPI = new MerchantCustomerUserApi(merchantConfig, baseOptions.baseURL, service)
export const merchantCreditExchangeRecordAPI = new MerchantCreditExchangeRecordApi(merchantConfig, baseOptions.baseURL, service)
export const merchantWmsStockAPI = new MerchantWmsStockApi(merchantConfig, baseOptions.baseURL, service)
export const merchantWmsDataControllerAPI = new MerchantWmsDataControllerApi(merchantConfig, baseOptions.baseURL, service)
export const merchantWmsConsumptionOrderAPI = new MerchantWmsConsumptionOrderApi(merchantConfig, baseOptions.baseURL, service)
export const merchantWmsSkuAPI = new MerchantWmsSkuApi(merchantConfig, baseOptions.baseURL, service)
export const merchantWmsSkuCategoryAPI = new MerchantWmsSkuCategoryApi(merchantConfig, baseOptions.baseURL, service)
export const merchantCreditChangeRecordAPI = new MerchantCreditChangeRecordApi(merchantConfig, baseOptions.baseURL, service)
export const merchantMmsMemberLevelAPI = new MerchantMmsMemberLevelApi(merchantConfig, baseOptions.baseURL, service)
export const memberStatAPI = new MemberStatApi(merchantConfig, baseOptions.baseURL, service)
export const memberAdminAPI = new MemberAdminApi(merchantConfig, baseOptions.baseURL, service)
export const memberCashierAPI = new MemberCashierApi(merchantConfig, baseOptions.baseURL, service)
export const merchantMemberChargeRecordAPI = new MerchantMemberChargeRecordApi(merchantConfig, baseOptions.baseURL, service)
export const merchantMemberOptionRecordAPI = new MerchantMemberOptionRecordApi(merchantConfig, baseOptions.baseURL, service)
export const merchantPurchaseAPI = new MerchantPurchaseApi(merchantConfig, baseOptions.baseURL, service)
export const storedValueCardCashierAPI = new StoredValueCardCashierApi(merchantConfig, baseOptions.baseURL, service)
export const cashbackCashierAPI = new CashbackCashierApi(merchantConfig, baseOptions.baseURL, service)
export const memberCashierCompensationAPI = new MemberCashierCompensationApi(merchantConfig, baseOptions.baseURL, service)
export const couponAPI = new CouponApi(merchantConfig, baseOptions.baseURL, service)
export const chainEmployeeBindingsAPI = new ChainEmployeeBindingsApi(merchantConfig, baseOptions.baseURL, service)
export const merchantHeadquarterBusinessReportAPI = new MerchantHeadquarterBusinessReportControllerApi(merchantConfig, baseOptions.baseURL, service)
